
import React, { useState, useMemo } from 'react';
import DayCard from './DayCard';
import RotateCcwIcon from './icons/RotateCcwIcon';
import RecipeSelectorModal from './RecipeSelectorModal';
import TrashIcon from './icons/TrashIcon';
import ChevronUpIcon from './icons/ChevronUpIcon';
import BulkShoppingListModal from './BulkShoppingListModal';
import PlanOverviewCard from './PlanOverviewCard';
import RecipeUsageModal from './RecipeUsageModal';
import { attachRecipeToPlannedMeal } from '../utils/plannedMealRecipe';
import { useCookingSessionStatuses } from '../hooks/useCookingSessionStatuses';
import {
    aggregateRecipeUsage,
    calculateMeatGramsFromPlannedMeals,
    collectMealsFromPlan,
} from '../utils/planUsageStats';
import type { MealPlan, Recipe, DayOfWeek, PlannedMeal, Role, MealType, HouseholdRole, Language, ActiveShoppingCart, ShoppingCategory, UserMealPreferences, Chef, User, Household } from '../types';
import { getTranslation } from '../utils/translations';
import { DAYS_OF_WEEK } from '../types';
import { formatWeekRange, weekRangeDir } from '../utils/dateFormat';
import PlannerHeader from './PlannerHeader';

interface WeeklyPlannerProps {
    viewMode: 'week' | 'month';
    setViewMode: (mode: 'week' | 'month') => void;
    onInfoClick?: () => void;
    mealPlan: MealPlan;
    recipes: Recipe[];
    role: Role;
    householdRole: HouseholdRole;
    weekOffset: number;
    currentUserId: string;
    activeHouseholdId?: string;
    activeHousehold?: Household | null;
    onSetWeekOffset: (offset: number) => void;
    onAddRecipesToDay: (recipeIds: string[], day: DayOfWeek, mealType: MealType) => void;
    onScheduleMeal?: (recipeId: string, day: DayOfWeek, mealType: MealType, servings: number, status: 'buy' | 'pantry' | 'prepared', weekOffset: number) => Promise<void> | void;
    onRemoveRecipeFromDay: (plannedMealId: string, day: DayOfWeek) => void;
    onMoveMeal: (plannedMealId: string, fromDay: DayOfWeek, toDay: DayOfWeek, toType: MealType) => void;
    onUpdateMeal: (plannedMealId: string, day: DayOfWeek, updates: { servings?: number; status?: 'buy' | 'pantry' | 'prepared' }) => void;
    onGenerateWeeklyShoppingList: () => void;
    onGenerateMealShoppingList: (meals: (PlannedMeal & { recipe: Recipe })[]) => void;
    onOpenMeatList: () => void;
    onOpenReminders: () => void;
    onEditRecipe: (recipeId: string) => void;
    onDeleteRecipe: (recipeId: string) => void;
    onCopyCurrentWeekToNext: () => void;
    onRecipeClick: (recipe: Recipe) => void;
    visibleMealTypes?: MealType[];
    language?: Language;
    orderedDays?: DayOfWeek[];
    onCookMeal: (meals: (PlannedMeal & { recipe: Recipe })[], type: MealType, day?: DayOfWeek, weekOffset?: number, slotMenuTitles?: Partial<Record<string, string>>, initialRecipeId?: string) => void;
    onEditMeal?: (meal: PlannedMeal & { recipe: Recipe }) => void;
    onUpdateMealSlotTitle?: (day: DayOfWeek, mealType: MealType, title: string | null) => void;
    onToggleFavoriteRecipe?: (recipeId: string) => void;
    onCreateRecipe?: (name: string) => Promise<string | void>;
    activeCart?: ActiveShoppingCart | null;
    onUpdateActiveCart?: (cart: ActiveShoppingCart) => Promise<void>;
    categoryOverrides?: Record<string, ShoppingCategory>;
    userMealPreferences?: UserMealPreferences | null;
    chefs?: Chef[];
    allUsers?: User[];
}

const WeeklyPlanner: React.FC<WeeklyPlannerProps> = ({ 
    viewMode, setViewMode, onInfoClick = () => {},
    mealPlan, recipes, role, householdRole, weekOffset, currentUserId, activeHouseholdId, activeHousehold = null, onSetWeekOffset,
    onAddRecipesToDay, onScheduleMeal, onRemoveRecipeFromDay, onMoveMeal, onUpdateMeal,
    onGenerateWeeklyShoppingList, onGenerateMealShoppingList, onOpenMeatList, onOpenReminders, onEditRecipe, onDeleteRecipe,
    onRecipeClick, visibleMealTypes = ['Lunch', 'Dinner'], language, orderedDays, onCookMeal, onEditMeal,
    onUpdateMealSlotTitle,
    activeCart, onUpdateActiveCart, categoryOverrides = {},
    userMealPreferences = null, chefs = [], allUsers = [],
    onToggleFavoriteRecipe,
    onCreateRecipe,
}) => {
    const canManage = role === 'superuser' || ['owner', 'admin', 'editor'].includes(householdRole);
    const t = (key: any) => getTranslation(language, key);
    const cookingSessionStatuses = useCookingSessionStatuses(activeHouseholdId);

    const [isDraggingMeal, setIsDraggingMeal] = useState(false);
    const [selectorTarget, setSelectorTarget] = useState<{day: DayOfWeek, type: MealType} | null>(null);
    const [showShoppingListModal, setShowShoppingListModal] = useState(false);
    const [isUsageModalOpen, setIsUsageModalOpen] = useState(false);

    const currentWeekDates = useMemo(() => {
        const now = new Date();
        const startDayName = orderedDays?.[0] || 'Monday';
        const startDayIndex = startDayName === 'Sunday' ? 0 : 1;
        const currentDayIndex = now.getDay();
        
        let diff = currentDayIndex - startDayIndex;
        if (diff < 0) diff += 7;
        
        const startOfWeek = new Date(now);
        startOfWeek.setDate(now.getDate() - diff + (weekOffset * 7));
        startOfWeek.setHours(0, 0, 0, 0);
        
        const dates = [];
        for (let i = 0; i < 7; i++) {
            const d = new Date(startOfWeek);
            d.setDate(startOfWeek.getDate() + i);
            dates.push(d);
        }
        return dates;
    }, [weekOffset, orderedDays]);

    const weekRangeLabel = useMemo(
        () => formatWeekRange(currentWeekDates[0], currentWeekDates[6], language),
        [currentWeekDates, language],
    );
    
    const handleOpenRecipeSelector = (day: DayOfWeek, type: MealType) => {
        setSelectorTarget({ day, type });
    };

    const mealTypeLabel = (type: MealType) =>
        type === 'Breakfast' ? t('morning') : type === 'Lunch' ? t('noon') : t('evening');

    const daysToRender = orderedDays || ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'] as DayOfWeek[];

    const todayIndex = useMemo(() => {
        if (weekOffset !== 0) return -1;
        const now = new Date();
        const todayName = DAYS_OF_WEEK[now.getDay()]; // DAYS_OF_WEEK is 0=Sun...6=Sat
        return daysToRender.indexOf(todayName);
    }, [weekOffset, daysToRender]);

    const handleTrashDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        const movedMealData = e.dataTransfer.getData("application/planned-meal-json");
        if (movedMealData) {
            const { id, day } = JSON.parse(movedMealData);
            onRemoveRecipeFromDay(id, day);
        }
        setIsDraggingMeal(false);
    };

    const weekMeals = useMemo(
        () => collectMealsFromPlan(mealPlan, daysToRender),
        [mealPlan, daysToRender],
    );

    const { populatedDays, recipeUsage, totalMeatWeight } = useMemo(() => {
        const activeDays = daysToRender.filter((day) => (mealPlan[day] || []).length > 0).length;
        return {
            populatedDays: activeDays,
            recipeUsage: aggregateRecipeUsage(weekMeals, recipes),
            totalMeatWeight: calculateMeatGramsFromPlannedMeals(weekMeals, recipes),
        };
    }, [daysToRender, mealPlan, weekMeals, recipes]);

    return (
        <div className="flex-1 flex flex-col relative h-full overflow-hidden bg-slate-100 dark:bg-slate-950 font-rubik transition-colors duration-200">
            <div className="flex-1 overflow-y-auto pb-32">
                <PlannerHeader
                    viewMode={viewMode}
                    setViewMode={setViewMode}
                    dateLabel={weekRangeLabel}
                    onPrevDate={() => onSetWeekOffset(weekOffset - 1)}
                    onNextDate={() => onSetWeekOffset(weekOffset + 1)}
                    isCurrentPeriod={weekOffset === 0}
                    onReturnToCurrent={() => onSetWeekOffset(0)}
                    onSyncClick={() => setShowShoppingListModal(true)}
                    onInfoClick={onInfoClick}
                    onMenuClick={() => setIsUsageModalOpen(true)}
                    language={language}
                />

                <div className="flex flex-col gap-y-4 p-4">
                    {daysToRender.map((day, index) => {
                        const plannedMeals = mealPlan[day] || [];
                        const dayMeals = plannedMeals.map(pm =>
                            attachRecipeToPlannedMeal(pm, recipes, t('unknownRecipe')),
                        );
                        
                        const isPassed = weekOffset === 0 && todayIndex !== -1 && index < todayIndex;
                        
                        return (
                            <DayCard
                                key={`${day}-${weekOffset}`} 
                                day={day} 
                                meals={dayMeals} 
                                role={role} 
                                canManage={canManage}
                                onDropRecipe={(recipeId, mealType) => onAddRecipesToDay([recipeId], day, mealType)}
                                onRemoveRecipe={(plannedMealId) => onRemoveRecipeFromDay(plannedMealId, day)}
                                onMoveMeal={onMoveMeal} 
                                onUpdateMeal={(pmId, updates) => onUpdateMeal(pmId, day, updates)}
                                onRecipeClick={onRecipeClick} 
                                onAddMeal={(type) => handleOpenRecipeSelector(day, type)}
                                onGenerateMealShoppingList={(meals) => onGenerateMealShoppingList(meals)} 
                                visibleMealTypes={visibleMealTypes}
                                language={language} 
                                onMealDragStart={() => setIsDraggingMeal(true)} 
                                onMealDragEnd={() => setIsDraggingMeal(false)}
                                onCookMeal={(meals, type, initialRecipeId) => onCookMeal(meals, type, day, weekOffset, mealPlan.slotMenuTitles, initialRecipeId)}
                                defaultExpanded={!isPassed}
                                onEditMeal={onEditMeal}
                                weekOffset={weekOffset}
                                cookingSessionStatuses={cookingSessionStatuses}
                                slotMenuTitles={mealPlan.slotMenuTitles}
                                onUpdateMealSlotTitle={onUpdateMealSlotTitle}
                            />
                        );
                    })}
                </div>
            </div>

            <div onDragOver={(e) => e.preventDefault()} onDrop={handleTrashDrop} 
                className={`absolute bottom-24 left-4 right-4 h-20 bg-red-100 dark:bg-red-900/30 border-2 border-dashed border-red-300 dark:border-red-800 rounded-[2rem] flex items-center justify-center text-red-600 dark:text-red-400 font-black gap-3 z-50 transition-all duration-300 transform ${isDraggingMeal ? 'translate-y-0 opacity-100' : 'translate-y-40 opacity-0 pointer-events-none'}`}>
                <TrashIcon className="w-8 h-8" /><span>{t('delete').toUpperCase()}</span>
            </div>

            {selectorTarget && (
                <RecipeSelectorModal 
                    isOpen={!!selectorTarget}
                    onClose={() => setSelectorTarget(null)}
                    recipes={recipes}
                    title={`${t('add')} - ${mealTypeLabel(selectorTarget.type)}`}
                    targetDay={selectorTarget.day}
                    targetMealType={selectorTarget.type}
                    weekOffset={weekOffset}
                    orderedDays={daysToRender}
                    visibleMealTypes={visibleMealTypes}
                    onConfirmSchedule={async (recipeId, day, type, servings, status, offset) => {
                        if (onScheduleMeal) {
                            await onScheduleMeal(recipeId, day, type, servings, status, offset);
                        } else {
                            onAddRecipesToDay([recipeId], day, type);
                        }
                    }}
                    activeHouseholdId={activeHouseholdId}
                    language={language}
                    userMealPreferences={userMealPreferences}
                    currentUserId={currentUserId}
                    chefs={chefs}
                    allUsers={allUsers}
                    activeHousehold={activeHousehold}
                    userRole={role}
                    householdRole={householdRole}
                    onToggleFavoriteRecipe={onToggleFavoriteRecipe}
                    onCreateRecipe={onCreateRecipe}
                />
            )}

            {showShoppingListModal && (
                <BulkShoppingListModal 
                    isOpen={showShoppingListModal}
                    onClose={() => setShowShoppingListModal(false)}
                    mealPlan={mealPlan}
                    recipes={recipes}
                    orderedDays={daysToRender}
                    language={language}
                    activeCart={activeCart}
                    onUpdateActiveCart={onUpdateActiveCart}
                    weeks={[currentWeekDates]}
                    categoryOverrides={categoryOverrides}
                />
            )}

            <RecipeUsageModal
                isOpen={isUsageModalOpen}
                onClose={() => setIsUsageModalOpen(false)}
                usageData={recipeUsage}
                populatedDays={populatedDays}
                totalDays={daysToRender.length}
                meatWeight={totalMeatWeight}
                titleKey="weeklyRecipeUsage"
                language={language}
            />
        </div>
    );
};
export default WeeklyPlanner;
